/**
 * CR313: Category Rail for Feature Flags
 * 
 * Left sidebar with category navigation, counts, and active section highlight.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, FolderOpen } from 'lucide-react';
import { FEATURE_FLAG_CATEGORIES, type FeatureFlagCategory } from '@/lib/admin/featureFlagsRegistry';
import type { FeatureFlagState } from '@/hooks/admin/useFeatureFlagsAdmin';

interface CategoryCounts {
  total: number;
  default: number;
  overridden: number;
}

interface FeatureFlagsCategoryRailProps {
  flags: FeatureFlagState[];
  activeCategory: FeatureFlagCategory | 'Other' | null;
  showUnregistered: boolean;
  unregisteredCount: number;
  onCategoryClick: (category: FeatureFlagCategory | 'Other') => void;
  onToggleUnregistered: () => void;
}

function getCategoryCounts(flags: FeatureFlagState[], category: FeatureFlagCategory): CategoryCounts {
  const categoryFlags = flags.filter(f => f.registryEntry?.category === category);
  return {
    total: categoryFlags.length,
    default: categoryFlags.filter(f => !f.isOverridden).length,
    overridden: categoryFlags.filter(f => f.isOverridden).length,
  };
}

export function FeatureFlagsCategoryRail({
  flags,
  activeCategory,
  showUnregistered,
  unregisteredCount,
  onCategoryClick,
  onToggleUnregistered,
}: FeatureFlagsCategoryRailProps) {
  return (
    <nav className="w-56 shrink-0 border-r bg-muted/30 p-3 space-y-1">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 mb-2">
        Categories
      </div>
      
      {FEATURE_FLAG_CATEGORIES.map(category => {
        const counts = getCategoryCounts(flags, category);
        const isActive = activeCategory === category;
        const isEmpty = counts.total === 0;
        
        return (
          <button
            key={category}
            onClick={() => !isEmpty && onCategoryClick(category)}
            disabled={isEmpty}
            className={cn(
              'w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm transition-colors',
              isActive && 'bg-primary/10 text-primary font-medium',
              !isActive && !isEmpty && 'hover:bg-muted text-foreground',
              isEmpty && 'opacity-50 cursor-not-allowed text-muted-foreground'
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <ChevronRight className={cn(
                'h-3 w-3 shrink-0 transition-transform',
                isActive && 'rotate-90'
              )} />
              <span className="truncate">{category}</span>
            </div>
            
            <div className="flex items-center gap-1 shrink-0">
              <Badge 
                variant="secondary" 
                className="h-5 min-w-[20px] px-1.5 text-xs font-normal"
              >
                {counts.total}
              </Badge>
              {counts.overridden > 0 && (
                <Badge 
                  variant="default" 
                  className="h-5 min-w-[20px] px-1.5 text-xs font-normal bg-amber-500/80"
                >
                  {counts.overridden}
                </Badge>
              )}
            </div>
          </button>
        );
      })}
      
      {/* Other (Unregistered) */}
      {unregisteredCount > 0 && (
        <>
          <div className="border-t my-2" />
          <button
            onClick={onToggleUnregistered}
            className={cn(
              'w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm transition-colors',
              activeCategory === 'Other' && 'bg-primary/10 text-primary font-medium',
              activeCategory !== 'Other' && 'hover:bg-muted text-muted-foreground'
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <FolderOpen className="h-3 w-3 shrink-0" />
              <span className="truncate">Other (Unregistered)</span>
            </div>
            <Badge 
              variant="outline" 
              className="h-5 min-w-[20px] px-1.5 text-xs font-normal"
            >
              {unregisteredCount}
            </Badge>
          </button>
        </>
      )}
    </nav>
  );
}
